import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MatchingDetailPanel } from './MatchingDetailPanel';
import { LienOrganisation } from '../repertoire/LienOrganisation';
import { Carte, Chargement, MessageErreur, Pastille } from '../ui/Primitives';
import { useHistoriqueMatching } from '../../lib/useMatching';
import { ErreurApi } from '../../lib/api';

function jour(valeur: string): string {
  return new Date(valeur).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

const LIBELLES_STATUT: Record<string, { texte: string; ton: string }> = {
  PROPOSEE: { texte: 'Proposée', ton: 'info' },
  ACCEPTEE: { texte: 'Acceptée', ton: 'success' },
  REFUSEE: { texte: 'Écartée', ton: 'danger' },
  EXPIREE: { texte: 'Expirée', ton: 'neutral' },
};

/**
 * Historique des propositions et de leur issue.
 *
 * C'est la matière du réglage des poids : une tranche de score qui ne convertit
 * pas dit que le barème surestime quelque chose. Une tranche sans décision
 * n'affiche pas de taux plutôt que zéro — rien n'y a encore été arbitré.
 */
export function HistoriqueMatching({ site }: { site: string }): JSX.Element {
  const { data, isLoading, error } = useHistoriqueMatching(site);

  if (error) {
    return (
      <MessageErreur
        message={
          error instanceof ErreurApi ? error.message : 'L’historique n’a pas pu être chargé.'
        }
      />
    );
  }
  if (isLoading) return <Chargement libelle="Chargement de l’historique…" />;

  const compteurs = data?.compteurs;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Compteur valeur={compteurs?.PROPOSEE ?? 0} libelle="en attente d'arbitrage" />
        <Compteur valeur={compteurs?.ACCEPTEE ?? 0} libelle="acceptée(s)" />
        <Compteur valeur={compteurs?.REFUSEE ?? 0} libelle="écartée(s)" />
        <Compteur valeur={compteurs?.EXPIREE ?? 0} libelle="expirée(s)" />
      </div>

      <Carte titre="Conversion par tranche de score" sansBordure>
        <div className="relative overflow-x-auto">
          <table className="w-full min-w-[700px] text-sm">
            <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-codialis-gray">
              <tr>
                {['Tranche', 'Proposées', 'Acceptées', 'Écartées', 'Taux d’acceptation'].map(
                  (colonne) => (
                    <th key={colonne} className="px-4 py-3 text-left font-bold">
                      {colonne}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data?.tranches ?? []).map((tranche) => (
                <tr key={tranche.plage}>
                  <td className="px-4 py-3 font-mono text-xs text-codialis-navy">
                    {tranche.plage}
                  </td>
                  <td className="px-4 py-3 text-codialis-gray">{tranche.proposees}</td>
                  <td className="px-4 py-3 text-codialis-gray">{tranche.acceptees}</td>
                  <td className="px-4 py-3 text-codialis-gray">{tranche.refusees}</td>
                  <td className="px-4 py-3">
                    {tranche.tauxAcceptation === null ? (
                      <span className="text-xs text-codialis-gray">aucune décision</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="progress-track h-1.5 w-20 overflow-hidden rounded-full">
                          <div
                            className="progress-bar h-full"
                            style={{ width: `${tranche.tauxAcceptation}%` }}
                          />
                        </div>
                        <span className="font-mono text-xs font-bold text-codialis-navy">
                          {tranche.tauxAcceptation} %
                        </span>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="px-4 py-3 text-[11px] text-codialis-gray md:px-6">
          Une tranche haute qui s'écarte souvent signale un critère surévalué dans le barème ; le
          réglage se fait depuis « Critères de matching ».
        </p>
      </Carte>

      <Carte titre="Dernières décisions" sansBordure>
        <div className="divide-y divide-gray-100">
          {(data?.decisions ?? []).map((decision) => (
            <LigneDecision key={decision.id} decision={decision} />
          ))}

          {(data?.decisions ?? []).length === 0 && (
            <p className="p-4 sm:p-6 text-sm text-codialis-gray">
              Aucune proposition tranchée sur ce périmètre.
            </p>
          )}
        </div>
      </Carte>
    </div>
  );
}

function Compteur({ valeur, libelle }: { valeur: number; libelle: string }): JSX.Element {
  return (
    <div className="rounded-2xl bg-white p-4 shadow-card">
      <div className="font-display text-2xl font-extrabold text-codialis-navy">{valeur}</div>
      <div className="text-xs text-codialis-gray">{libelle}</div>
    </div>
  );
}

function LigneDecision({ decision }: { decision: any }): JSX.Element {
  const [deplie, setDeplie] = useState(false);
  const libelle = LIBELLES_STATUT[decision.statut];

  return (
    <div className="flex flex-col gap-2 p-4 md:px-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/adm-dossier/${decision.candidat.id}`}
              className="text-sm font-semibold text-codialis-navy hover:text-codialis-sky"
            >
              {decision.candidat.nom}
            </Link>
            <span className="text-sm text-codialis-gray">
              → <LienOrganisation id={decision.entreprise.id} nom={decision.entreprise.denomination} classeLien="hover:text-codialis-sky hover:underline transition" />
            </span>
            <Pastille
              texte={libelle?.texte ?? decision.statut}
              ton={libelle?.ton ?? 'neutral'}
            />
          </div>
          <div className="mt-0.5 text-[11px] text-codialis-gray">
            {decision.score} / 100 · proposé le {jour(decision.creeLe)}
            {decision.decideLe ? ` · tranché le ${jour(decision.decideLe)}` : ''}
            {decision.origine === 'DEMANDE' ? ' · calcul à la demande' : ''}
          </div>
          {decision.motifRefus && (
            <p className="mt-1 text-[11px] text-codialis-gray">
              Motif : {decision.motifRefus}
            </p>
          )}
        </div>
        
        <button
          type="button"
          onClick={() => setDeplie((valeur) => !valeur)}
          className="whitespace-nowrap text-xs font-semibold text-codialis-sky hover:underline"
        >
          {deplie ? 'Masquer le détail' : 'Détail du score'}
        </button>
      </div>

      {deplie && (
        decision.details ? (
          <MatchingDetailPanel adequation={decision.details} />
        ) : (
          <div className="mt-3 grid grid-cols-1 gap-1 border-t border-gray-100 pt-3 sm:grid-cols-2">
            {decision.criteres?.map((critere: any) => (
              <div
                key={critere.critere}
                className="flex items-baseline justify-between gap-3 text-[11px]"
              >
                <span className="text-codialis-gray">
                  {critere.critere} · {critere.motif}
                </span>
                <span className="whitespace-nowrap font-mono text-codialis-navy">
                  {critere.points}/{critere.maximum}
                </span>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
